import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FireSimple,
  X,
  CaretRight,
} from "@phosphor-icons/react";
import { parseDate, workstreamColor } from "@/lib/gantt-utils";
import { EVENT_ICON_TYPES, iconForEvent } from "@/lib/event-icons";

// ISO week number + year
function isoWeek(date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), week: weekNum };
}

function weekKey(date) {
  const { year, week } = isoWeek(date);
  return `${year}-W${String(week).padStart(2, "0")}`;
}

function weekStart(date) {
  // Monday of that week (ISO)
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dow = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() - (dow - 1));
  return d;
}

function weekLabel(date) {
  const s = weekStart(date);
  return s.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

export default function RiskHeatmap({ open, onOpenChange, project, tasks, onJumpToTask }) {
  const [monthsBack, setMonthsBack] = useState("all");
  // Multi-select: which icon types to roll up. Defaults to risk + issue (the original behaviour).
  const [allowedTypes, setAllowedTypes] = useState(() => new Set(["risk", "issue"]));
  const [activeCell, setActiveCell] = useState(null); // {ws, wk}

  const toggleType = (t) => {
    setAllowedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };
  const setAllTypes = () => setAllowedTypes(new Set(EVENT_ICON_TYPES.map((o) => o.type)));
  const setRiskIssue = () => setAllowedTypes(new Set(["risk", "issue"]));

  const analysis = useMemo(() => {
    const workstreams = Array.from(
      new Set(tasks.map((t) => t.workstream).filter(Boolean))
    ).sort();
    if (workstreams.length === 0) {
      workstreams.push("Unassigned");
    }

    // Gather events filtered by allowed types
    const rows = [];
    tasks.forEach((t) => {
      const ws = t.workstream || "Unassigned";
      (t.events || []).forEach((ev) => {
        if (!allowedTypes.has(ev.type)) return;

        // Effective date: event_date → field_ref date → first date → created_at
        let dateStr = null;
        if (ev.event_date) dateStr = ev.event_date;
        else if (ev.field_ref && t.dates?.[ev.field_ref]) {
          dateStr = t.dates[ev.field_ref];
        } else if (t.dates) {
          for (const v of Object.values(t.dates)) {
            if (v) { dateStr = v; break; }
          }
        }
        const d = dateStr ? parseDate(dateStr) : (ev.created_at ? parseDate(ev.created_at.slice(0, 10)) : null);
        if (!d) return;
        rows.push({ task: t, event: ev, ws, date: d });
      });
    });

    // Apply time window filter
    const filtered = rows.filter((r) => {
      if (monthsBack === "all") return true;
      const n = parseInt(monthsBack, 10);
      const cutoff = new Date();
      cutoff.setUTCMonth(cutoff.getUTCMonth() - n);
      return r.date >= cutoff;
    });

    // Determine the set of weeks present
    const weekMap = new Map(); // key -> {key, start, label}
    filtered.forEach((r) => {
      const k = weekKey(r.date);
      if (!weekMap.has(k)) {
        const ws = weekStart(r.date);
        weekMap.set(k, { key: k, start: ws, label: weekLabel(r.date), wkNum: isoWeek(r.date).week });
      }
    });

    // Also include weeks with no events between min/max so the heatmap reads as a timeline
    if (weekMap.size > 0) {
      const sorted = Array.from(weekMap.values()).sort((a, b) => a.start - b.start);
      const first = sorted[0].start;
      const last = sorted[sorted.length - 1].start;
      const cur = new Date(first);
      while (cur <= last) {
        const k = weekKey(cur);
        if (!weekMap.has(k)) {
          weekMap.set(k, {
            key: k,
            start: new Date(cur),
            label: weekLabel(cur),
            wkNum: isoWeek(cur).week,
          });
        }
        cur.setUTCDate(cur.getUTCDate() + 7);
      }
    }

    const weeks = Array.from(weekMap.values()).sort((a, b) => a.start - b.start);

    // Bucket by (workstream, weekKey)
    const cells = new Map(); // `${ws}::${weekKey}` -> events[]
    filtered.forEach((r) => {
      const key = `${r.ws}::${weekKey(r.date)}`;
      if (!cells.has(key)) cells.set(key, []);
      cells.get(key).push(r);
    });

    // Compute max for intensity
    let maxCount = 0;
    cells.forEach((arr) => { if (arr.length > maxCount) maxCount = arr.length; });

    return { workstreams, weeks, cells, maxCount, total: filtered.length };
  }, [tasks, allowedTypes, monthsBack]);

  const cellEvents = activeCell
    ? analysis.cells.get(`${activeCell.ws}::${activeCell.wk}`) || []
    : [];

  const handleJump = (taskId) => {
    onJumpToTask?.(taskId);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-md max-w-6xl p-0 gap-0 overflow-hidden"
        data-testid="risk-heatmap-dialog"
      >
        <DialogHeader className="p-6 pb-4 border-b border-slate-200">
          <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <FireSimple size={22} weight="fill" className="text-red-500" />
            Risk &amp; Issue Heatmap
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Executive roll-up of risk + issue icons, grouped by workstream × week.
            Click any cell to inspect the underlying events.
          </DialogDescription>
        </DialogHeader>

        {/* Controls */}
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 shrink-0 space-y-2.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest font-mono-ibm text-slate-500 mr-1">
              Filter by icon
            </span>
            {EVENT_ICON_TYPES.map((o) => {
              const Icon = o.icon;
              const active = allowedTypes.has(o.type);
              return (
                <button
                  key={o.type}
                  type="button"
                  onClick={() => toggleType(o.type)}
                  className={`inline-flex items-center gap-1 h-7 px-2 rounded-md border text-[11px] font-medium transition-all ${
                    active
                      ? "border-transparent text-white shadow-sm"
                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                  }`}
                  style={active ? { background: o.color } : {}}
                  data-testid={`heatmap-type-${o.type}`}
                  title={o.description}
                >
                  <Icon size={11} weight={active ? "fill" : "bold"} />
                  <span>{o.label}</span>
                </button>
              );
            })}
            <div className="flex-1" />
            <button
              type="button"
              onClick={setRiskIssue}
              className="text-[10px] font-mono-ibm uppercase tracking-widest text-slate-500 hover:text-slate-950"
              data-testid="heatmap-preset-risks"
            >
              Risks + Issues
            </button>
            <span className="text-slate-300">·</span>
            <button
              type="button"
              onClick={setAllTypes}
              className="text-[10px] font-mono-ibm uppercase tracking-widest text-slate-500 hover:text-slate-950"
              data-testid="heatmap-preset-all"
            >
              Select all
            </button>
          </div>

          <div className="flex items-center gap-3">
            <Select value={monthsBack} onValueChange={setMonthsBack}>
              <SelectTrigger
                className="h-9 w-[150px] rounded-md border-slate-300 text-sm focus:ring-0"
                data-testid="heatmap-range-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-md">
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="1">Last 1 month</SelectItem>
                <SelectItem value="3">Last 3 months</SelectItem>
                <SelectItem value="6">Last 6 months</SelectItem>
                <SelectItem value="12">Last 12 months</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex-1" />

            <div className="flex items-center gap-4 text-[10px] font-mono-ibm uppercase tracking-widest text-slate-500">
              <span>Total: <span className="text-slate-950 font-semibold">{analysis.total}</span></span>
              <div className="flex items-center gap-1.5">
                <span>Low</span>
                <div className="flex h-2.5 rounded-md overflow-hidden border border-slate-300">
                  {[0.1, 0.3, 0.5, 0.75, 1].map((t, i) => (
                    <div
                      key={i}
                      className="w-5 h-full"
                      style={{ background: intensityColor(t) }}
                    />
                  ))}
                </div>
                <span>High</span>
              </div>
            </div>
          </div>
        </div>

        {/* Grid + drawer */}
        <div className="flex h-[560px] overflow-hidden">
          <div className="flex-1 overflow-auto custom-scrollbar p-6">
            {analysis.total === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-slate-500">
                <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                  <FireSimple size={22} weight="fill" className="text-slate-400" />
                </div>
                <div className="text-sm font-medium text-slate-700">No risks or issues tracked yet</div>
                <div className="text-xs text-slate-500 mt-1 max-w-sm font-mono-ibm">
                  Hover a row in the gantt and click <span className="font-semibold">+</span> to add a risk or issue icon. They'll surface here.
                </div>
              </div>
            ) : (
              <table
                className="border-separate border-spacing-[2px] font-mono-ibm text-[11px]"
                data-testid="heatmap-table"
              >
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-white z-10 pr-2 text-left text-[10px] uppercase tracking-widest text-slate-500 font-medium align-bottom py-2">
                      Workstream
                    </th>
                    {analysis.weeks.map((w) => (
                      <th
                        key={w.key}
                        className="text-center text-[9px] uppercase tracking-widest text-slate-500 font-medium align-bottom py-2 min-w-[52px]"
                      >
                        <div className="text-slate-700">W{w.wkNum}</div>
                        <div className="text-slate-400 text-[9px] normal-case">{w.label}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {analysis.workstreams.map((ws) => {
                    const ink = workstreamColor(ws);
                    const rowTotal = analysis.weeks.reduce(
                      (acc, w) => acc + (analysis.cells.get(`${ws}::${w.key}`)?.length || 0),
                      0
                    );
                    return (
                      <tr key={ws} data-testid={`heatmap-row-${ws}`}>
                        <th className="sticky left-0 bg-white z-10 pr-3 py-1.5 text-left">
                          <div className="flex items-center gap-2">
                            <span className={`h-2 w-2 rounded-full ${ink.dot}`} />
                            <span className="text-xs text-slate-800 font-semibold font-sans whitespace-nowrap">{ws}</span>
                            <span className="text-[10px] text-slate-400 ml-auto">{rowTotal}</span>
                          </div>
                        </th>
                        {analysis.weeks.map((w) => {
                          const key = `${ws}::${w.key}`;
                          const count = analysis.cells.get(key)?.length || 0;
                          const intensity = analysis.maxCount > 0 ? count / analysis.maxCount : 0;
                          const isActive = activeCell?.ws === ws && activeCell?.wk === w.key;
                          return (
                            <td
                              key={w.key}
                              className="p-0"
                              style={{ minWidth: 52 }}
                            >
                              <button
                                type="button"
                                onClick={() => setActiveCell(count > 0 ? { ws, wk: w.key } : null)}
                                className={`w-full h-8 rounded-md text-[10px] font-medium transition-all ${
                                  count > 0 ? "cursor-pointer hover:scale-[1.08]" : "cursor-default"
                                } ${isActive ? "ring-2 ring-slate-950 ring-offset-1" : ""}`}
                                style={{
                                  background: intensityColor(intensity),
                                  color: intensity > 0.55 ? "#fff" : "#475569",
                                }}
                                data-testid={`heatmap-cell-${ws}-${w.key}`}
                                title={`${ws} · ${w.label} · ${count} event${count === 1 ? "" : "s"}`}
                              >
                                {count > 0 ? count : ""}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Drawer */}
          {activeCell && (
            <div className="w-[320px] border-l border-slate-200 bg-white flex flex-col shrink-0 fade-up">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-mono-ibm text-slate-500">
                    {activeCell.ws}
                  </div>
                  <div className="text-sm font-semibold text-slate-950 truncate">
                    {analysis.weeks.find((w) => w.key === activeCell.wk)?.label} · {activeCell.wk}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveCell(null)}
                  className="h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
                  data-testid="close-cell-drawer"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar divide-y divide-slate-100">
                {cellEvents.map((row) => {
                  const meta = iconForEvent(row.event);
                  const Icon = meta.icon;
                  const color = row.event.color || meta.color;
                  return (
                    <div key={row.event.id} className="p-3">
                      <div className="flex items-start gap-2">
                        <div
                          className="h-5 w-5 rounded-md flex items-center justify-center shrink-0 border-2 border-white shadow-sm"
                          style={{ background: color }}
                        >
                          <Icon size={10} weight="fill" className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold text-slate-950 uppercase tracking-wide">
                            {row.event.icon_label || meta.label}
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono-ibm mt-0.5">
                            {row.task.task || row.task.subtask || "Untitled task"}
                          </div>
                          {row.event.comment && (
                            <div className="text-xs text-slate-700 mt-1.5 leading-relaxed">
                              {row.event.comment}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => handleJump(row.task.id)}
                            className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-950 font-medium font-mono-ibm uppercase tracking-widest"
                            data-testid={`jump-to-task-${row.task.id}`}
                          >
                            Jump to task <CaretRight size={10} weight="bold" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {cellEvents.length === 0 && (
                  <div className="p-6 text-center text-xs text-slate-500">
                    No events in this cell.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function intensityColor(t) {
  if (t <= 0) return "#f1f5f9"; // slate-100
  // Interpolate from slate-100 to red-600
  const stops = [
    { t: 0, rgb: [241, 245, 249] },
    { t: 0.2, rgb: [254, 226, 226] },  // red-100
    { t: 0.4, rgb: [252, 165, 165] },  // red-300
    { t: 0.7, rgb: [239, 68, 68] },    // red-500
    { t: 1.0, rgb: [153, 27, 27] },    // red-800
  ];
  let lo = stops[0], hi = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (t >= stops[i].t && t <= stops[i + 1].t) {
      lo = stops[i]; hi = stops[i + 1]; break;
    }
  }
  const range = hi.t - lo.t || 1;
  const local = (t - lo.t) / range;
  const rgb = lo.rgb.map((c, i) => Math.round(c + (hi.rgb[i] - c) * local));
  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
}
